import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { HashLink } from 'react-router-hash-link';
import { Redirect } from 'react-router-dom';
import { autoTab, CMSContent, getCMSObject, TeleScript, IfMobile, ErrorMessage, FindDrawNumbers, AlertBox, DisplayContent } from 'sg-ui-components';
import { mapStateToProps, mapDispatchToProps } from '../../Store';
import { Button, Modal } from 'react-bootstrap';
import { parsePrepopulatedTicket } from '../../utils/parsePrepopulatedTicketNumber';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import * as yup from 'yup';

import IGTOnlyScanner from './IGTOnlyScanner';

library.add(faCircleInfo, faCircleXmark);

/**********************************************************************
 * Component:  DrawTicketForm
 * Purpose:    Allows for the entry of Draw Tickets
 *
 * Props:       user - the user data store
 *              actions - store actions (apis)
 *              drawTicket - the draw ticket data store
 *
 * APIs used:   drawTicketActions.enterDrawTicket
 *
 *  Notes:  For MO, does not check Winning Status, only for
 *          bonusing rewards.
 */
const DrawTicketForm = ({ user, actions, drawTicket, prepopulatedTicket, cmsSourceFirebase, config }) => {
    // Format of the Draw Tickets
    const initialDrawFields = JSON.parse(JSON.stringify(config?.site?.tickets?.drawFields ?? []));
    const ticketEntryTeleScript = getCMSObject('data.ticketEntry');
    const telescript = getCMSObject('data.components.teleScripts.drawTicketForm.jsonBlock');

    const [drawFields, setDrawFields] = useState(initialDrawFields);
    const [ticketError, setTicketError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [ticketSuccess, setTicketSuccess] = useState(false);
    const [showModel, setShowModel] = useState(false);
    const [repaintValue, setRepaintValue] = useState(0);
    const elemRefs = [];

    useEffect(() => {
        if (prepopulatedTicket) {
            let currentNumber = prepopulatedTicket;
            // Get the Game Number first
            drawFields.forEach((field) => {
                currentNumber = parsePrepopulatedTicket(currentNumber, field);
            });
        }
    }, [prepopulatedTicket]);

    //***********************************************************************************
    // Processes the ticket status and if the ticket was a success, redirect to
    // Post Claim.
    // If Ticket fails, report the error.  If failue was due to Player Lockout, prevent
    // further submitting of tickets.
    //
    useEffect(() => {
        if (disabled) {
            if (drawTicket.success) {
                updateDashboard();
                setTicketSuccess(true);
            } else {
                setTicketError(drawTicket.message);
            }
            setDisabled(false);
        }
        if (drawTicket.message === '_PLAYER_LOCKOUT') {
            setDisabled(true);
        }
    }, [drawTicket]);

    // force a repaint of the screen when the drawFields have changed (via a
    // prepopulated ticket).  This sets a state of a variable that will force
    // the repaint of the screen.
    //
    useEffect(() => {
        setRepaintValue(repaintValue + 1);
    }, [drawFields]);

    //***********************************************************************************
    // Updates the dashboard on a ticket entry, see if points have been updated
    //
    const updateDashboard = async () => {
        if (user.loggedIn && user.player.actions.length === 0) {
            await actions.userActions.getDashboard();
        }
    };

    //***********************************************************************************
    // Cycle through the draw fields and set up the input boxes.
    // Autotabbing should be allowed to go from input to input.
    const DrawFieldInputs = () => {
        return drawFields.map((field, index) => {
            const ref = React.createRef();
            elemRefs.push(ref);
            return (
                <div className='d-inline ticket-number-inputs' key={`draw-fields--${index}`}>
                    <input
                        type='text'
                        data-index={index}
                        ref={ref}
                        id={field.name}
                        className='theme-input mb-2 mb-sm-0'
                        placeholder={field.placeholder}
                        size={field.size}
                        maxLength={field.size}
                        defaultValue={field.value}
                        onChange={(event) => handleFieldChange(event, field)}
                        onKeyUp={(event) => autoTab(event, elemRefs)}
                        required
                    />
                    <span className='dash'>&mdash;</span>
                </div>
            );
        });
    }; // end DrawFieldInputs

    //***********************************************************************************
    // onChange callback for the input fields.    Sets the state of drawFields.
    //
    const handleFieldChange = async (event, field) => {
        let fields = drawFields;

        const value = event.target.value;
        const fieldIndex = fields.findIndex((obj) => obj.name == field.name);
        fields[fieldIndex].value = value;
        setDrawFields(fields);
    }; // end handleFieldChange

    //***********************************************************************************
    // Handle the submit of the draw ticket.  Combine all the tickets fields into
    // one string object that is passed to the DrawTicket API and disable submit
    // until we get a response.
    //
    const handleDrawSubmit = async () => {
        setDisabled(true);
        setValidationError('');
        setTicketSuccess(false);
        let ticketString = '';

        drawFields.forEach((field) => {
            ticketString = ticketString + field.value;
        });

        const drawEntrySchema = yup
            .string()
            .required(telescript?.validationTicketIsRequired ?? 'Please completely fill out all of the entry fields.')
            .min(config?.site?.tickets?.drawTicketLength, telescript?.validationTicketNotFilled ?? 'Please completely fill out all of the entry fields.');

        const valid = await drawEntrySchema.isValid(ticketString);

        if (valid) {
            await actions.drawTicketActions.enterDrawTicket({
                ticket_number: ticketString,
                entry_method: 'manual',
                ticket_type: 'draw',
            });
        } else {
            await drawEntrySchema.validate(ticketString, { abortEarly: true }).catch(function (err) {
                setValidationError(err.message);
            });
        }
        setDisabled(false);
    }; // end handleDrawSubmit

    const showDrawTooltip = () => {
        setShowModel(true);
    };

    const handleModalClose = () => {
        setShowModel(false);
    };

    const resetDrawTicketFormStates = () => {
        setTicketError('');
        setValidationError('');
    };

    return (
        <div>
            {ticketSuccess ? <Redirect to='/post-claim' /> : null}

            <DisplayContent note='Display local errors when validation fails.' isVisible={validationError}>
                <AlertBox message={validationError} />
            </DisplayContent>

            <ErrorMessage code={ticketError} collection='data.messages.ticketErrorMessages.jsonBlock' />

            <IfMobile>
                <IGTOnlyScanner resetParentState={resetDrawTicketFormStates} />
            </IfMobile>

            <div className='enter-form text-center mt-3 mb-1'>
                <DrawFieldInputs />
                <FontAwesomeIcon icon='fa-solid fa-circle-info' className='ticket-tooltip' onClick={() => showDrawTooltip()} />
            </div>

            <div className='enter-form text-center theme-text-default mb-3'>
                <TeleScript line={ticketEntryTeleScript?.drawTicketEntryInstructions?.contentHTML}>
                    Enter 25-digit alpha-numeric Webcode in box on ticket.
                </TeleScript>
            </div>

            <div className='enter-form text-center'>
                <button type='submit' className='btn theme-btn theme-primary' disabled={disabled} onClick={() => handleDrawSubmit()}>
                    Submit
                </button>
                <HashLink to='/my-activity#ticket-history' className='view-history-link d-block mt-1'>
                    <TeleScript line={ticketEntryTeleScript?.ticketHistoryLink?.contentHTML}>View your ticket history</TeleScript>
                </HashLink>
            </div>

            <Modal show={showModel} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <CMSContent
                            localStorageObject='webContent'
                            contentPath='data.ticketEntry.findDrawNumbers.contentHeaderText'
                            cmsSourceFirebase={cmsSourceFirebase}
                        />
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <FindDrawNumbers.DrawNumbersExample cmsSourceFirebase={cmsSourceFirebase} />
                </Modal.Body>

                <Modal.Footer>
                    <Button className='btn theme-btn theme-secondary' onClick={handleModalClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    ); // end return
};

export default connect(mapStateToProps, mapDispatchToProps)(DrawTicketForm);
